import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit, Trash2, Clock, Package, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMushroomsPage() {
  const { authHeader } = useAuth();
  const [mushrooms, setMushrooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      const r = await axios.get(`${API}/admin/mushrooms`, authHeader());
      setMushrooms(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await axios.delete(`${API}/admin/mushrooms/${id}`, authHeader());
    setMushrooms(m => m.filter(x => x.id !== id));
  };

  const toggleUpcoming = async (m) => {
    const updated = { ...m, isUpcoming: !m.isUpcoming };
    await axios.put(`${API}/admin/mushrooms/${m.id}`, updated, authHeader());
    setMushrooms(ms => ms.map(x => x.id === m.id ? updated : x));
  };

  const toggleActive = async (m) => {
    const updated = { ...m, isActive: !m.isActive };
    await axios.put(`${API}/admin/mushrooms/${m.id}`, updated, authHeader());
    setMushrooms(ms => ms.map(x => x.id === m.id ? updated : x));
  };

  if (loading) return (
    <div className="p-4 sm:p-8"><div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-16 rounded-xl border border-[#273A2F] animate-pulse" style={{ backgroundColor: '#16201A' }} />)}</div></div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-mushrooms-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#F2F4F3]">Mushrooms</h1>
          <p className="text-sm text-[#A8BCAE] mt-1">{mushrooms.length} varieties managed</p>
        </div>
        <Link to="/admin/mushrooms/new" data-testid="add-mushroom-btn">
          <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white" style={{ backgroundColor: '#D97A46' }}>
            <Plus className="w-4 h-4" /> Add New
          </button>
        </Link>
      </div>

      <div className="rounded-2xl border border-[#273A2F] overflow-hidden" style={{ backgroundColor: '#16201A' }}>
        <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-[#273A2F]">
              {["Image & Name", "Category", "Price / UOM", "Qty", "Upcoming", "Active", "Actions"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#A8BCAE] uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#273A2F]">
            {mushrooms.map(m => (
              <tr key={m.id} className="hover:bg-white/[0.02] transition-colors" data-testid={`mushroom-row-${m.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={m.image} alt={m.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-[#F2F4F3]">{m.name}</p>
                      <p className="text-xs text-[#A8BCAE] italic">{m.scientificName}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full border border-[#273A2F] text-[#A8BCAE] capitalize">{m.category}</span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-[#D97A46]">Rs.{m.price}</p>
                  <p className="text-xs text-[#A8BCAE]">per {m.uom}</p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 text-sm text-[#F2F4F3]">
                    <Package className="w-3.5 h-3.5 text-[#A8BCAE]" />
                    {m.qty}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`toggle-upcoming-${m.id}`}
                    onClick={() => toggleUpcoming(m)}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${m.isUpcoming ? "border-amber-500/30 text-amber-400 bg-amber-400/10" : "border-emerald-500/30 text-emerald-400 bg-emerald-400/10"}`}
                  >
                    {m.isUpcoming ? <><Clock className="w-3 h-3" /> Upcoming</> : "In Stock"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`toggle-active-${m.id}`}
                    onClick={() => toggleActive(m)}
                    title={m.isActive !== false ? "Click to hide from customers" : "Click to show to customers"}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${m.isActive !== false ? "border-emerald-500/30 text-emerald-400 bg-emerald-400/10" : "border-red-500/30 text-red-400 bg-red-400/10"}`}
                  >
                    {m.isActive !== false
                      ? <><Eye className="w-3 h-3" /> Active</>
                      : <><EyeOff className="w-3 h-3" /> Hidden</>
                    }
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/mushrooms/${m.id}/edit`} data-testid={`edit-mushroom-${m.id}`}>
                      <button className="p-1.5 rounded-lg text-[#A8BCAE] hover:text-[#F2F4F3] hover:bg-white/5 transition-all">
                        <Edit className="w-4 h-4" />
                      </button>
                    </Link>
                    <button
                      data-testid={`delete-mushroom-${m.id}`}
                      onClick={() => handleDelete(m.id, m.name)}
                      className="p-1.5 rounded-lg text-[#A8BCAE] hover:text-red-400 hover:bg-red-400/10 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {mushrooms.length === 0 && (
          <div className="text-center py-16 text-[#A8BCAE]">No mushrooms yet. Add your first one!</div>
        )}
      </div>
    </div>
  );
}
